import { useTranslation } from "react-i18next";
import { CircularProgress, makeStyles } from "@material-ui/core";
import { UIToggle } from "@View/components/common/UIToggle";
import { StopFactorSetting } from "@Constants/types";

const BORDER = "#E1E6F1";
const MUTED = "#A1ADBA";

const useStyles = makeStyles(() => ({
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: "10px",
    marginBottom: "8px",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "17px",
    color: MUTED,
  },
  count: {
    fontWeight: 600,
    color: "#1A1E24",
  },
  links: {
    display: "flex",
    gap: "6px",
  },
  link: {
    padding: 0,
    border: "none",
    background: "none",
    font: "inherit",
    color: "#54606F",
    cursor: "pointer",
    "&:hover": {
      color: "#1A1E24",
      textDecoration: "underline",
    },
    "&:disabled": {
      color: MUTED,
      cursor: "default",
      textDecoration: "none",
    },
  },
  list: {
    marginBottom: "16px",
    border: `1px solid ${BORDER}`,
    borderRadius: "8px",
    overflow: "hidden",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 15px",
    borderBottom: `1px solid ${BORDER}`,
    cursor: "pointer",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      background: "#FAFBFC",
    },
  },
  number: {
    flexShrink: 0,
    width: "20px",
    fontWeight: 500,
    fontSize: "13px",
    color: MUTED,
  },
  name: {
    flex: 1,
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "18px",
    color: "#1A1E24",
  },
  nameDisabled: {
    color: MUTED,
  },
}));

type StopFactorsSettingsSectionProps = {
  /** null — настройки ещё загружаются */
  settings: StopFactorSetting[] | null;
  onChange: (key: string, isEnabled: boolean) => void;
};

const StopFactorsSettingsSection = ({ settings, onChange }: StopFactorsSettingsSectionProps) => {
  const { t } = useTranslation();
  const classes = useStyles();

  if (settings === null) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
        <CircularProgress size={28} />
      </div>
    );
  }

  const enabledCount = settings.filter((s) => s.isEnabled).length;

  const setAll = (isEnabled: boolean) =>
    settings.filter((s) => s.isEnabled !== isEnabled).forEach((s) => onChange(s.key, isEnabled));

  return (
    <div>
      <div className={classes.toolbar}>
        <span>
          {t("StopFactors.enabledCount")}{" "}
          <span className={classes.count}>
            {enabledCount} / {settings.length}
          </span>
        </span>
        <span className={classes.links}>
          <button
            type="button"
            className={classes.link}
            disabled={enabledCount === settings.length}
            onClick={() => setAll(true)}
          >
            {t("StopFactors.enableAll")}
          </button>
          ·
          <button
            type="button"
            className={classes.link}
            disabled={enabledCount === 0}
            onClick={() => setAll(false)}
          >
            {t("StopFactors.disableAll")}
          </button>
        </span>
      </div>

      <div className={classes.list}>
        {settings.map((setting, index) => (
          <label key={setting.key} className={classes.row}>
            <span className={classes.number}>{index + 1}.</span>
            <span className={`${classes.name} ${setting.isEnabled ? "" : classes.nameDisabled}`}>
              {setting.displayName}
            </span>
            <UIToggle
              checked={setting.isEnabled}
              onChange={(event) => onChange(setting.key, event.target.checked)}
            />
          </label>
        ))}
      </div>
    </div>
  );
};

export default StopFactorsSettingsSection;
